import {
  forwardRef,
  type Ref,
  useState,
  type FocusEvent,
  type ChangeEvent,
  useEffect,
  type ReactElement,
  createRef,
  type ForwardRefRenderFunction,
  type InputHTMLAttributes,
} from 'react';
import styled, { css } from 'styled-components';
import type { FieldHelperProps } from 'formik';

import Layout from '@yoweb/ui/components/form/Layout';
import { CaretDownThickIcon } from '@yoweb/ui/components/Icon/CaretDownThickIcon';
import { CloseIcon } from '@yoweb/ui/components/Icon/CloseIcon';
import type { TextSizeProps } from '@yoweb/ui/components/typography/Text';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { getColor, getSpace } from '@yoweb/ui/styles/utils/theme';
import SelectDropdown, {
  type SelectOptions,
  type WrapperSelectDropdownProps,
} from '@yoweb/ui/components/form/SelectDropdown';
import { ButtonIcon } from '@yoweb/ui/components/buttons';
import { formInputCommon, formStylesCss } from '@yoweb/ui/components/form/formStyles';

type DefaultSelectUIProps = {
  id: string;
  name: string;
  label?: string;
  isRequired?: boolean;
  error?: string;
  caption?: ReactElement | string;
  options: SelectOptions[];
  onClick?(value: unknown): void;
  onChangeCallback?(value: unknown): void;
  onDelete?(): void;
  deleteAriaLabel?: string;
  maxHeightOptions?: number;
  hideErrorMessage?: boolean;
} & DataTestId;

export type SelectUIProps = InputHTMLAttributes<HTMLSelectElement> &
  TextSizeProps &
  DefaultSelectUIProps;

/**
 * A styled select element.
 */
const SelectUI: ForwardRefRenderFunction<
  HTMLSelectElement,
  SelectUIProps & FieldHelperProps<unknown>
> = (
  {
    id,
    name,
    onFocus,
    onChange,
    onBlur,
    options,
    setValue,
    error,
    label,
    disabled,
    placeholder,
    isRequired = false,
    onChangeCallback,
    onDelete,
    deleteAriaLabel = 'remove',
    hideErrorMessage,
    ...props
  },
  ref: Ref<HTMLSelectElement>,
) => {
  const wrapperRef = createRef<HTMLDivElement>();
  const [isFocused, setFocus] = useState(false);
  const [hasValue, setHasValue] = useState(Boolean(props?.defaultValue || props?.value));
  const [isHoverable, setIsHoverable] = useState(false);

  const onChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    setHasValue(event.target.value.trim().length > 0);

    if (onChange) {
      onChange(event);
      onChangeCallback?.(event.target.value);
    }
  };

  const onFocusHandler = (event: FocusEvent<HTMLSelectElement>) => {
    setFocus(true);

    if (onFocus) {
      onFocus(event);
    }
  };

  const onBlurHandler = (event: FocusEvent<HTMLSelectElement>) => {
    setFocus(false);

    // Prevent firing onBlur when an option is selected, since for required select inputs
    // it'll cause the form validation to kick in, causing UI jank - only check this when
    // we're not using the native select.
    if (onBlur && (!isHoverable || !wrapperRef.current?.contains(event.relatedTarget))) {
      onBlur(event);
    }
  };

  useEffect(() => {
    // TODO: this will result in a bug when values become clearable
    // but a defaultValue was provided
    setHasValue(Boolean(props.value || props.defaultValue));
  }, [props.value, props.defaultValue]);

  useEffect(() => {
    if (wrapperRef.current) {
      // Since we are using a mouse, it's safe to show the custom select.
      // If the computer is using keyboard the let's hide back the custom select while the native one is visible
      setIsHoverable(window.matchMedia('(hover: hover)').matches);
    }
  }, [wrapperRef]);

  const hasLabel = !!label;
  const showPlaceholder = !disabled && !hasLabel;
  const hasError = !!error;
  const hasDelete = !!onDelete && hasValue;

  return (
    <Layout
      isFocused={isFocused}
      hasValue={hasValue}
      id={id || name}
      isRequired={isRequired}
      hideErrorMessage={hideErrorMessage}
      error={error}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      {...props}
    >
      <StyledSelectWrapper
        hasError={hasError}
        hasFocus={isFocused}
        hasDelete={hasDelete}
        ref={wrapperRef}
        isDisabled={disabled}
      >
        {!isHoverable ? (
          <StyledSelect
            id={id}
            name={name}
            disabled={disabled}
            hasError={!!error}
            hasLabel={hasLabel}
            hasPlaceholder={!!placeholder}
            hasValue={hasValue}
            onFocus={onFocusHandler}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            {...props}
            ref={ref}
            $deviceNoHover={!isHoverable}
          >
            <PlaceholderOption value="" hidden>
              {showPlaceholder && placeholder}
            </PlaceholderOption>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledSelect>
        ) : (
          <SelectDropdown
            id={id}
            label={label}
            placeholder={placeholder}
            error={error}
            setValue={setValue}
            options={options}
            onBlur={onBlurHandler}
            onFocus={onFocusHandler}
            wrapperComponent={StyledSelect}
            disabled={disabled}
            data-testid={props['data-testid']}
            {...props}
            onClick={(selectedValue) => onChangeCallback?.(selectedValue)}
          />
        )}
        <Actions>
          <DeleteButtonWrap>
            <ButtonIcon
              disabled={!hasDelete}
              onClick={onDelete}
              isFloating={false}
              size="sm"
              variant="tertiary"
              aria-label={deleteAriaLabel}
              icon={<CloseIcon size={16} />}
              type="button"
            />
          </DeleteButtonWrap>
          <ButtonIcon
            size="sm"
            as="span"
            variant="tertiary"
            type="button"
            isFloating={false}
            icon={<Caret size={16} disabled={!!disabled} />}
          />
        </Actions>
      </StyledSelectWrapper>
    </Layout>
  );
};

const StyledSelect = styled.select<WrapperSelectDropdownProps>`
  ${formInputCommon}

  padding-left: ${getSpace('normal1')};
  width: 100%;
  height: 100%;
  height: ${pxToRem(56)};
  white-space: nowrap;

  &:hover {
    background: none;
  }

  &:focus {
    outline: none;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      ${formStylesCss.disabled}
    `}

  ${({ hasValue }) => css`
    color: ${getColor(hasValue ? 'base900' : 'base700')};
    padding-top: ${getSpace('normal2')};
  `}

  ${({ $deviceNoHover }) =>
    $deviceNoHover &&
    css`
      padding-top: ${getSpace('normal1')};
      padding-bottom: 0;
    `}
`;

const PlaceholderOption = styled.option`
  color: ${getColor('base700')};
`;

const Actions = styled.div`
  align-items: center;
  bottom: 0;
  column-gap: ${pxToRem(6)};
  display: flex;
  grid-auto-flow: column;
  height: 100%;
  pointer-events: none;
  position: absolute;
  right: ${getSpace('small3')};
  top: 0;
`;

const Caret = styled(CaretDownThickIcon)<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${getColor('baseA300')};
    `}
`;

const DeleteButtonWrap = styled.div`
  display: none;
  opacity: 0;
`;

const StyledSelectWrapper = styled.div<{
  hasError: boolean;
  hasFocus?: boolean;
  hasDelete?: boolean;
  isDisabled?: boolean;
}>`
  transform-origin: top;

  ${({ hasFocus, hasDelete }) =>
    hasFocus &&
    hasDelete &&
    css`
      && ${DeleteButtonWrap} {
        display: flex;
        opacity: 1;
        pointer-events: all;
      }
    `}

  &:hover {
    ${({ hasDelete }) =>
      hasDelete &&
      css`
        && ${DeleteButtonWrap} {
          display: flex;
          opacity: 1;
          pointer-events: all;
        }
      `}
  }
`;

export default forwardRef(SelectUI);
